import { deserialize } from "flatgeobuf/lib/mjs/geojson";
import { inject, injectable } from "inversify";
import debounce from "lodash/debounce";
import type maplibregl from "maplibre-gl";
import { EngineConfigService, type HandlerOf, type IService, Logger } from "@designboticteam/core";
import {
	evaluateParcelFilters,
	getGeometryCenter,
	getRingCenter,
	hasActiveParcelFilters,
	MapConfigService,
	MapStoreService,
	PARCEL_COLUMNS,
	type ParcelFilters,
	type ParcelInfo,
	type ParcelPropertiesInfo,
	type ParcelSort,
	PmTilesService,
	VisibleParcels,
} from "@designboticteam/map";
import { MapLibreInstanceService } from "../../core/MapLibreInstanceService";
import { PmTilesRenderingService } from "../../rendering/PmTilesRenderingService";
import {
	PARCELS_FGB_PATH,
	PARCELS_FILL_LAYER_ID,
	PARCELS_OUTLINE_LAYER_ID,
	PARCELS_PMTILES_PATH,
	PARCELS_PMTILES_SOURCE_ID,
	PARCELS_SOURCE_LAYER,
} from "./constants";
import { createParcelsSourceDefinition, ParcelStyleController } from "./ParcelStyleController";
import { ParcelsListClient, type ParcelsListResult } from "./parcelsList";
import { parseParcelInfo, parseParcelPropertiesInfo } from "./utils/parseParcelInfo";

const VISIBLE_PARCELS_DEBOUNCE_MS = 1000;

@injectable()
class MapLibreParcelsDataManager implements IService {
	public readonly serviceName = "MapLibreParcelsDataService";
	private readonly logger = new Logger(`🧪 ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#ff00ff",
	});

	public isHighlightingActive = false;
	private storeUnsubscribeFunctions: Array<() => void> = [];
	private listHighlightedParcelId: string | null = null;

	private listClient: ParcelsListClient | null = null;
	private sort: ParcelSort = { key: "id", direction: "asc" };
	private matchingParcelIds = new Set<string>();

	private readonly styleController: ParcelStyleController;

	constructor(
		@inject(EngineConfigService) private readonly engineConfigService: EngineConfigService,
		@inject(MapConfigService) private readonly mapConfigService: MapConfigService,
		@inject(PmTilesService) private readonly pmTilesService: PmTilesService,
		@inject(MapStoreService) private readonly mapStoreService: MapStoreService,
		@inject(MapLibreInstanceService) private readonly mapLibreInstanceService: MapLibreInstanceService,
		@inject(PmTilesRenderingService) private readonly pmTilesRenderingService: PmTilesRenderingService,
	) {
		this.styleController = new ParcelStyleController({
			mapLibreInstanceService: this.mapLibreInstanceService,
			mapStoreService: this.mapStoreService,
			palette: this.engineConfigService.palette,
		});

		this.logger.log("Constructed.");
	}

	/**
	 * Registers the parcels source and wires up the map and store events.
	 */
	public initialize(): void {
		if (PARCELS_PMTILES_PATH === undefined || PARCELS_FGB_PATH === undefined) {
			this.logger.warn(
				"Parcels disabled: set VITE_PARCELS_PMTILES_PATH and VITE_PARCELS_FGB_PATH to enable them.",
				`(pmtiles: ${PARCELS_PMTILES_PATH ?? "missing"}, fgb: ${PARCELS_FGB_PATH ?? "missing"})`,
			);
			return;
		}

		this.pmTilesService.addLayer(createParcelsSourceDefinition({ palette: this.engineConfigService.palette, url: PARCELS_PMTILES_PATH }));

		// The list is built off the archive in a worker, so the main thread never walks the parcels itself.
		if (this.mapConfigService.isParcelsListEnabled === true) {
			this.listClient = new ParcelsListClient({
				url: PARCELS_PMTILES_PATH,
				sourceLayer: PARCELS_SOURCE_LAYER,
				onResult: this.handleListResult,
				onError: (message) => this.logger.error(`Parcels list worker failed: ${message}`),
			});
		}

		this.storeUnsubscribeFunctions = [
			// onFeatureHovered
			this.pmTilesRenderingService.onFeatureHovered.subscribe(this.handleOnFeatureHovered).unsubscribe,

			// onFeaturePicked
			this.pmTilesRenderingService.onFeaturePicked.subscribe(this.handleOnFeaturePicked).unsubscribe,

			// onLayerStackChanged
			this.pmTilesRenderingService.onLayerStackChanged.subscribe(this.styleController.raiseLayers).unsubscribe,

			// parcels layer switched on or off
			this.mapStoreService.subscribeToState(
				(state) => state.sources.pmTiles.get(PARCELS_PMTILES_SOURCE_ID)?.displaySettings.isVisible,
				() => this.updateVisibleParcels(),
			),

			// hovered parcel
			this.mapStoreService.subscribeToState(
				(state) => state.parcels.hoveredParcelId,
				(hoveredParcelId) => this.applyListHoverToMap(hoveredParcelId),
			),

			// favourites, because the list can be sorted by them
			this.mapStoreService.subscribeToState(
				(state) => state.parcels.selectedParcels,
				() => {
					if (this.sort.key === "favourite") {
						this.updateVisibleParcels();
					}
				},
			),

			// parcels source finished loading its archive
			this.mapStoreService.subscribeToState(
				(state) => state.sources.pmTiles.get(PARCELS_PMTILES_SOURCE_ID)?.isInitialized,
				(isInitialized) => {
					if (isInitialized === true) {
						this.styleController.applyLabels();
					}
				},
			),
		];

		this.mapLibreInstanceService.map.on("moveend", this.handleMapMoveEnd);

		this.updateVisibleParcels();

		this.logger.log("Initialized.");
	}

	/** Mirrors the parcel under the cursor into the store, so the results list can follow it. */
	private handleOnFeatureHovered: HandlerOf<typeof this.pmTilesRenderingService.onFeatureHovered> = ({ features }) => {
		const parcelFeature = features.find((feature) => feature.source === PARCELS_PMTILES_SOURCE_ID);
		const hoveredParcelId = parcelFeature?.id !== undefined ? parcelFeature.id.toString() : null;
		this.setHoveredParcelId(hoveredParcelId);
	};

	/** Opens the clicked parcel, or closes it when the same parcel is clicked again. */
	private handleOnFeaturePicked: HandlerOf<typeof this.pmTilesRenderingService.onFeaturePicked> = async ({ features, lngLat }) => {
		const parcelFeature = features.find((feature) => feature.source === PARCELS_PMTILES_SOURCE_ID);

		if (parcelFeature === undefined) {
			return;
		}

		const properties = parcelFeature.properties ?? {};
		const id = properties?.parcel__id_dzialki;

		if (id === undefined) {
			return;
		}

		// A second click on the active parcel closes it.
		if (this.mapStoreService.state.parcels.activeParcel?.id === id) {
			this.setActiveParcel(null);
			return;
		}

		const bbox = { minX: lngLat.lng, minY: lngLat.lat, maxX: lngLat.lng, maxY: lngLat.lat };

		try {
			for await (const fgbFeature of deserialize(PARCELS_FGB_PATH, bbox)) {
				if (fgbFeature.properties?.parcel__id_dzialki !== id) {
					continue;
				}

				const parcelInfo = parseParcelInfo(fgbFeature as GeoJSON.Feature<GeoJSON.MultiPolygon>);

				if (parcelInfo !== null) {
					this.setActiveParcel(parcelInfo);
				}

				return;
			}
		} catch (error) {
			this.logger.error(`Failed to read picked parcel ${id} from FGB: ${error}`);
		}
	};

	/** Refreshes the visible parcels once the map has stopped moving. */
	private readonly handleMapMoveEnd = (): void => {
		this.updateVisibleParcels();
	};

	/** Shows or hides the whole parcels layer. */
	public setVisible(isVisible: boolean): void {
		this.pmTilesService.updateSourceDisplaySettings(PARCELS_PMTILES_SOURCE_ID, { isVisible });
	}

	/** Turns pointer picking on the parcels layer on or off. */
	public setHoverable(isHoverable: boolean): void {
		this.pmTilesService.updateSourceDisplaySettings(PARCELS_PMTILES_SOURCE_ID, { isHoverable });
	}

	/** Stores one filter value and rebuilds the list of visible parcels. */
	public setFilter<K extends keyof ParcelFilters>(key: K, value: ParcelFilters[K]): void {
		this.mapStoreService.setState((state) => ({
			parcels: {
				...state.parcels,
				filters: {
					...state.parcels.filters,
					[key]: value,
				},
			},
		}));

		this.updateVisibleParcels();
	}

	/** Picks the numeric field that drives parcel height, or `null` to keep the parcels flat. */
	public setVisualizationHeightField(key: string | null): void {
		this.mapStoreService.setState((state) => ({
			parcels: { ...state.parcels, visualization: { ...state.parcels.visualization, heightField: key } },
		}));

		this.styleController.apply();
	}

	/** Vertical exaggeration for a height field in metres, where 1 draws the plan's own value. */
	public setVisualizationHeightFactor(heightFactor: number): void {
		this.mapStoreService.setState((state) => ({
			parcels: { ...state.parcels, visualization: { ...state.parcels.visualization, heightFactor } },
		}));

		this.styleController.apply();
	}

	/** Picks the numeric field that drives parcel colour, or `PARCEL_COLOR_PUM_DIFF_KEY`, or `null` for the plain tint. */
	public setVisualizationColorField(key: string | null): void {
		this.mapStoreService.setState((state) => ({
			parcels: { ...state.parcels, visualization: { ...state.parcels.visualization, colorField: key } },
		}));

		this.styleController.apply();
	}

	/** Sets both ends of the PUM-difference ramp, where `pog` marks parcels with the higher POG value. */
	public setVisualizationPumDiffColors(colors: { pog: string; mpzp: string }): void {
		this.mapStoreService.setState((state) => ({
			parcels: {
				...state.parcels,
				visualization: { ...state.parcels.visualization, pumDiffColors: colors },
			},
		}));

		this.styleController.apply();
	}

	/** Sets both ends of the height-difference ramp, where `pog` marks parcels the POG lets build taller. */
	public setVisualizationHeightDiffColors(colors: { pog: string; mpzp: string }): void {
		this.mapStoreService.setState((state) => ({
			parcels: {
				...state.parcels,
				visualization: { ...state.parcels.visualization, heightDiffColors: colors },
			},
		}));

		this.styleController.apply();
	}

	/** Paints every parcel outside any MPZP red, on top of whatever colour mode is active. */
	public setVisualizationHighlightParcelsOutsideMpzp(isHighlighted: boolean): void {
		this.mapStoreService.setState((state) => ({
			parcels: { ...state.parcels, visualization: { ...state.parcels.visualization, highlightParcelsOutsideMpzp: isHighlighted } },
		}));

		this.styleController.apply();
	}

	/** Shows or hides the parcel-number labels. */
	public setLabelsVisible(isVisible: boolean): void {
		this.mapStoreService.setState((state) => ({ layers: { ...state.layers, parcelLabels: isVisible } }));

		this.styleController.applyLabels();
	}

	/** The hovered parcel is the one under the cursor, either on the map or in the results list. */
	public setHoveredParcelId(hoveredParcelId: string | null): void {
		if (this.mapStoreService.state.parcels.hoveredParcelId === hoveredParcelId) {
			return;
		}

		this.mapStoreService.setState((state) => ({ parcels: { ...state.parcels, hoveredParcelId } }));
	}

	/** The active parcel is the one the user clicked, and its details panel is open. */
	private setActiveParcel(parcel: ParcelInfo | null): void {
		if (this.mapStoreService.state.parcels.activeParcel?.id === parcel?.id) {
			return;
		}

		this.mapStoreService.setState((state) => ({ parcels: { ...state.parcels, activeParcel: parcel, hoveredSplitZoneIndex: null } }));
	}

	/** Closes the details panel, with the same effect as clicking the parcel a second time. */
	public clearActiveParcel(): void {
		this.setActiveParcel(null);
	}

	/** Adds the parcel to the favourites list, or removes it when it is already there. */
	public toggleParcelSelection(parcel: ParcelInfo): void {
		this.mapStoreService.setState((state) => {
			const isSelected = state.parcels.selectedParcels.some((selected) => selected.id === parcel.id);

			const selectedParcels = isSelected
				? state.parcels.selectedParcels.filter((selected) => selected.id !== parcel.id)
				: [...state.parcels.selectedParcels, parcel];

			return { parcels: { ...state.parcels, selectedParcels } };
		});
	}

	/** The same toggle for callers that only hold an id, which reads the geometry from the FGB before adding. */
	public async toggleParcelSelectionById(parcelId: string): Promise<void> {
		const isSelected = this.mapStoreService.state.parcels.selectedParcels.some((parcel) => parcel.id === parcelId);

		if (isSelected === true) {
			this.mapStoreService.setState((state) => ({
				parcels: { ...state.parcels, selectedParcels: state.parcels.selectedParcels.filter((parcel) => parcel.id !== parcelId) },
			}));
			return;
		}

		const parcelInfo = await this.loadParcelInfoById(parcelId);

		if (parcelInfo === null) {
			this.logger.error(`Cannot add parcel ${parcelId} to selection: not found in FGB.`);
			return;
		}

		this.toggleParcelSelection(parcelInfo);
	}

	/** Reads a rendered parcel's full data from the FGB, and returns null when the parcel is not on screen. */
	private async loadParcelInfoById(parcelId: string): Promise<ParcelInfo | null> {
		const center = this.getParcelCenter(parcelId);

		if (center === null) {
			return null;
		}

		const bbox = { minX: center.lng, minY: center.lat, maxX: center.lng, maxY: center.lat };

		try {
			for await (const fgbFeature of deserialize(PARCELS_FGB_PATH, bbox)) {
				if (String(fgbFeature.properties?.parcel__id_dzialki) !== parcelId) {
					continue;
				}

				return parseParcelInfo(fgbFeature as GeoJSON.Feature<GeoJSON.MultiPolygon>);
			}
		} catch (error) {
			this.logger.error(`Failed to read parcel ${parcelId} from FGB: ${error}`);
		}

		return null;
	}

	/** Flies to a parcel and opens it, which only works when the parcel is on screen or already a favourite. */
	public async focusParcel({ parcelId }: { parcelId: string }): Promise<void> {
		const selectedParcel = this.mapStoreService.state.parcels.selectedParcels.find((parcel) => parcel.id === parcelId);
		const outline = selectedParcel?.polygons[0]?.outline;

		if (selectedParcel !== undefined && outline !== undefined && outline.length > 0) {
			// A favourite parcel already has its geometry in memory.
			const outlineCenter = getRingCenter(outline);
			this.mapLibreInstanceService.map.flyTo({ center: [outlineCenter.lng, outlineCenter.lat] });
			this.setActiveParcel(selectedParcel);
			return;
		}

		const center = this.getParcelCenter(parcelId);

		if (center === null) {
			this.logger.error(`Parcel ${parcelId} is not rendered, cannot focus it.`);
			return;
		}

		this.mapLibreInstanceService.map.flyTo({ center: [center.lng, center.lat] });

		const bbox = { minX: center.lng, minY: center.lat, maxX: center.lng, maxY: center.lat };

		try {
			for await (const fgbFeature of deserialize(PARCELS_FGB_PATH, bbox)) {
				if (String(fgbFeature.properties?.parcel__id_dzialki) !== parcelId) {
					continue;
				}

				const parcelInfo = parseParcelInfo(fgbFeature as GeoJSON.Feature<GeoJSON.MultiPolygon>);

				if (parcelInfo !== null) {
					this.setActiveParcel(parcelInfo);
				}

				return;
			}

			this.logger.error(`Parcel ${parcelId} not found in FGB.`);
		} catch (error) {
			this.logger.error(`Failed to read parcel ${parcelId} from FGB: ${error}`);
		}
	}

	/** The parcel centre in WGS84. */
	private getParcelCenter(parcelId: string): { lng: number; lat: number } | null {
		const feature = this.findParcelMapFeature(parcelId);
		return feature === null ? null : getGeometryCenter(feature.geometry);
	}

	/** Finds a parcel among the features currently rendered on screen. */
	private findParcelMapFeature(parcelId: string): maplibregl.MapGeoJSONFeature | null {
		const layerIds = [PARCELS_OUTLINE_LAYER_ID, PARCELS_FILL_LAYER_ID].filter((layerId) => this.mapLibreInstanceService.map.getLayer(layerId) !== undefined);

		if (layerIds.length === 0) {
			return null;
		}

		const feature = this.mapLibreInstanceService.map
			.queryRenderedFeatures({ layers: layerIds })
			.find((candidate) => String(candidate.properties?.parcel__id_dzialki) === parcelId);

		return feature ?? null;
	}

	/** Debounced, because panning fires this again on every `moveend`. */
	private readonly updateVisibleParcels = debounce(() => this._updateVisibleParcels(), VISIBLE_PARCELS_DEBOUNCE_MS);
	/** Asks the worker to rebuild the list of parcels in view. Nothing here touches the map. */
	private _updateVisibleParcels(): void {
		const parcelsSource = this.mapStoreService.state.sources.pmTiles.get(PARCELS_PMTILES_SOURCE_ID);

		if (parcelsSource?.displaySettings.isVisible !== true) {
			this.setVisibleParcels(VisibleParcels.empty());
			this.applyMatchingParcels(new Set());
			return;
		}

		this.styleController.apply();

		// Without the worker the list simply stays empty; the map still draws the parcels on its own.
		if (this.listClient === null) {
			return;
		}

		// The same floor MapLibre applies to the source: below it the map requests no parcel tiles, so neither does the list.
		if (parcelsSource.minzoom !== undefined && Math.floor(this.mapLibreInstanceService.map.getZoom()) < parcelsSource.minzoom) {
			this.setVisibleParcels(VisibleParcels.empty());
			this.applyMatchingParcels(new Set());
			return;
		}

		const bounds = this.mapLibreInstanceService.map.getBounds();

		this.listClient.request({
			bounds: { west: bounds.getWest(), south: bounds.getSouth(), east: bounds.getEast(), north: bounds.getNorth() },
			zoom: this.mapLibreInstanceService.map.getZoom(),
			filters: this.mapStoreService.state.parcels.filters,
			sort: this.sort,
			favouriteIds: this.mapStoreService.state.parcels.selectedParcels.map((parcel) => parcel.id),
		});
	}

	/** Publishes a finished list and moves the map's filter highlight onto it. */
	private readonly handleListResult = (result: ParcelsListResult): void => {
		const visibleParcels = new VisibleParcels({ columns: result.columns, length: result.length });

		this.setVisibleParcels(visibleParcels);

		// With no filter set the list is everything in view, and nothing on the map is highlighted.
		const matchingIds = new Set<string>();

		if (result.hasActiveFilters === true) {
			for (let index = 0; index < visibleParcels.length; index++) {
				matchingIds.add(visibleParcels.getId(index));
			}
		}

		this.applyMatchingParcels(matchingIds);

		this.compareWithRenderedFeatures(visibleParcels, result);
	};

	/**
	 * TEMPORARY — runs the old main-thread query beside the worker and reports every disagreement, so the
	 * new path is proven against the old one on real data before the old one goes. Remove once the
	 * divergence counter has stayed at zero across the Tricity at several zooms, with filters and without.
	 */
	private compareWithRenderedFeatures(visibleParcels: VisibleParcels, result: ParcelsListResult): void {
		const map = this.mapLibreInstanceService.map;
		const isFillVisible = map.getLayer(PARCELS_FILL_LAYER_ID) !== undefined && map.getLayoutProperty(PARCELS_FILL_LAYER_ID, "visibility") !== "none";
		const queryLayerId = isFillVisible ? PARCELS_FILL_LAYER_ID : PARCELS_OUTLINE_LAYER_ID;

		if (map.getLayer(queryLayerId) === undefined) {
			return;
		}

		const { filters } = this.mapStoreService.state.parcels;
		const isAnyFilterActive = hasActiveParcelFilters(filters);

		const renderedRows = new Map<string, ParcelPropertiesInfo>();

		for (const feature of map.queryRenderedFeatures({ layers: [queryLayerId] })) {
			if (isAnyFilterActive === true && evaluateParcelFilters(filters, feature.properties) === false) {
				continue;
			}

			const parcel = parseParcelPropertiesInfo(feature.properties);

			if (parcel !== null && renderedRows.has(parcel.id) === false) {
				renderedRows.set(parcel.id, parcel);
			}
		}

		const workerRows = new Map<string, ParcelPropertiesInfo>();

		for (let index = 0; index < visibleParcels.length; index++) {
			const row = visibleParcels.getRow(index);
			workerRows.set(row.id, row);
		}

		const onlyInWorker: Array<string> = [];
		const onlyOnMap: Array<string> = [];
		const differingFields: Array<string> = [];

		for (const [id, row] of workerRows) {
			const rendered = renderedRows.get(id);

			if (rendered === undefined) {
				onlyInWorker.push(id);
				continue;
			}

			for (const column of PARCEL_COLUMNS) {
				if (row[column] !== rendered[column]) {
					differingFields.push(`${id} ${column}: worker ${JSON.stringify(row[column])} vs mapa ${JSON.stringify(rendered[column])}`);
				}
			}
		}

		for (const id of renderedRows.keys()) {
			if (workerRows.has(id) === false) {
				onlyOnMap.push(id);
			}
		}

		const isIdentical = onlyInWorker.length === 0 && onlyOnMap.length === 0 && differingFields.length === 0;
		const summary = `worker ${workerRows.size} vs mapa ${renderedRows.size} | ${result.tileCount} kafli | worker ${Math.round(result.durationMs)}ms`;

		if (isIdentical === true) {
			this.logger.log(`✅ zgodne: ${summary}`);
			return;
		}

		// `onlyOnMap` is the one that matters: a parcel the map draws but the list omits is a real miss.
		// `onlyInWorker` is expected while tiles are still arriving, because the worker reads the archive.
		this.logger.warn(
			`⚠️ rozbieżność: ${summary}`,
			`| tylko w workerze ${onlyInWorker.length}`,
			`| tylko na mapie ${onlyOnMap.length}`,
			`| różniące się pola ${differingFields.length}`,
		);

		for (const id of onlyOnMap.slice(0, 5)) {
			this.logger.warn(`   tylko na mapie: ${id}`);
		}

		for (const difference of differingFields.slice(0, 5)) {
			this.logger.warn(`   ${difference}`);
		}
	}

	/**
	 * Moves `matchesFilter` onto the parcels that now match, addressing them by id alone. Setting state for
	 * a tile the map has not loaded yet is safe: MapLibre keeps it in the source cache and applies it when
	 * the tile arrives.
	 */
	private applyMatchingParcels(matchingIds: Set<string>): void {
		const map = this.mapLibreInstanceService.map;
		const target = { source: PARCELS_PMTILES_SOURCE_ID, sourceLayer: PARCELS_SOURCE_LAYER };

		for (const id of this.matchingParcelIds) {
			if (matchingIds.has(id) === false) {
				map.removeFeatureState({ ...target, id }, "matchesFilter");
			}
		}

		for (const id of matchingIds) {
			if (this.matchingParcelIds.has(id) === false) {
				map.setFeatureState({ ...target, id }, { matchesFilter: true });
			}
		}

		this.matchingParcelIds = matchingIds;
	}

	/** Re-sorts the list. The worker owns the ordering, so this is a new query rather than a local sort. */
	public setSort(sort: ParcelSort): void {
		this.sort = sort;
		this.updateVisibleParcels();
	}

	/** Writes the visible-parcels list to the store for the results panel. */
	private setVisibleParcels(visibleParcels: VisibleParcels): void {
		this.mapStoreService.setState((state) => ({ parcels: { ...state.parcels, visibleParcels } }));
	}

	/** Moves the map hover highlight to the parcel the store points at. */
	private applyListHoverToMap(hoveredParcelId: string | null): void {
		if (this.listHighlightedParcelId === hoveredParcelId) {
			return;
		}

		const map = this.mapLibreInstanceService.map;
		const target = { source: PARCELS_PMTILES_SOURCE_ID, sourceLayer: PARCELS_SOURCE_LAYER };

		if (this.listHighlightedParcelId !== null) {
			map.removeFeatureState({ ...target, id: this.listHighlightedParcelId }, "hover");
		}

		if (hoveredParcelId !== null) {
			map.setFeatureState({ ...target, id: hoveredParcelId }, { hover: true });
		}

		this.listHighlightedParcelId = hoveredParcelId;
	}

	/** Unsubscribes everything and cancels the pending refresh. */
	public dispose(): void {
		this.updateVisibleParcels.cancel();

		this.listClient?.dispose();
		this.listClient = null;
		this.matchingParcelIds = new Set();

		const map = this.mapLibreInstanceService.map;
		map.off("moveend", this.handleMapMoveEnd);

		this.pmTilesRenderingService.onFeaturePicked.unsubscribe(this.handleOnFeaturePicked);
		this.pmTilesRenderingService.onFeatureHovered.unsubscribe(this.handleOnFeatureHovered);
		this.storeUnsubscribeFunctions.forEach((unsubscribe) => {
			unsubscribe();
		});
		this.storeUnsubscribeFunctions = [];
		this.logger.log("Disposed.");
	}
}

export { MapLibreParcelsDataManager as MapLibreParcelsDataService, PARCELS_PMTILES_SOURCE_ID };
